/**
 * Production plan card (`garment.production`) — the capacity plan at the top of
 * the Production tab: unit / supervisor / planned start / target date / daily
 * piece target, with a planned-vs-required pace hint computed against the live
 * WIP balance. The planning step between PP-approval and the floor.
 *
 * @see packages/domain/src/garment/planning
 */
import { useState } from "react";
import { CalendarClock, Pencil, X } from "lucide-react";
import { Button, Input, toast } from "@flint/ui";
import { trpc } from "../../../lib/trpc.js";

interface ProductionPlanCardProps {
  styleId: string;
  /** Order quantity + QC-passed finished, to compute the balance & required pace. */
  orderQty: number;
  finishedTotal: number;
}

export function ProductionPlanCard({ styleId, orderQty, finishedTotal }: ProductionPlanCardProps) {
  const utils = trpc.useUtils();
  const query = trpc.planning.get.useQuery({ styleId });
  const [editing, setEditing] = useState(false);

  const upsert = trpc.planning.upsert.useMutation({
    onSuccess: async () => {
      await utils.planning.get.invalidate({ styleId });
      setEditing(false);
      toast.success("Production plan saved");
    },
    onError: (e: { message: string }) => toast.error("Couldn't save", { description: e.message }),
  });

  if (query.isLoading) return null;
  const plan = query.data ?? null;
  const balanceToFinish = Math.max(0, orderQty - finishedTotal);
  const pace = computePace(balanceToFinish, plan?.targetDate ?? null, plan?.dailyTargetPcs ?? null);

  if (editing) {
    return <PlanForm styleId={styleId} plan={plan} pending={upsert.isPending} onCancel={() => setEditing(false)} onSave={(v) => upsert.mutate(v)} />;
  }

  const hasPlan =
    plan !== null &&
    (plan.unit !== null ||
      plan.supervisor !== null ||
      plan.plannedStartDate !== null ||
      plan.targetDate !== null ||
      plan.dailyTargetPcs !== null);

  return (
    <div className="rounded-md border border-line bg-bg p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted">
          <CalendarClock size={12} aria-hidden /> Production plan
        </div>
        <button type="button" className="tag-add" onClick={() => setEditing(true)}>
          <Pencil size={10} aria-hidden /> {hasPlan ? "Edit" : "Set plan"}
        </button>
      </div>

      {!hasPlan ? (
        <p className="mt-2 text-xs leading-relaxed text-muted">
          No plan set. Assign the unit, supervisor, dates and a daily target so the floor knows the pace to hold.
        </p>
      ) : (
        <>
          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs sm:grid-cols-4">
            <PlanField label="Unit" value={plan.unit} />
            <PlanField label="Supervisor" value={plan.supervisor} />
            <PlanField label="Start" value={fmtDate(plan.plannedStartDate)} />
            <PlanField label="Target" value={fmtDate(plan.targetDate)} />
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-line pt-2 text-xs">
            <span className="text-muted">Daily target</span>
            <span className="font-mono font-semibold text-ink">{plan.dailyTargetPcs !== null ? `${plan.dailyTargetPcs.toLocaleString("en-IN")}/day` : "—"}</span>
            {pace.requiredDailyRate !== null && balanceToFinish > 0 ? (
              <>
                <span className="text-muted">· need</span>
                <span className="font-mono font-semibold text-ink-2">~{pace.requiredDailyRate.toLocaleString("en-IN")}/day</span>
                <span className="text-muted">for {balanceToFinish.toLocaleString("en-IN")} balance{pace.daysToTarget !== null ? ` in ${pace.daysToTarget}d` : ""}</span>
                {pace.onTrack !== null ? (
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${pace.onTrack ? "bg-emerald-50 text-ok" : "bg-amber-50 text-warn"}`}>
                    {pace.onTrack ? "on track" : "under-planned"}
                  </span>
                ) : null}
              </>
            ) : balanceToFinish === 0 ? (
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-ok">order complete</span>
            ) : pace.overdue ? (
              <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-bad">target passed</span>
            ) : null}
          </div>
          {plan.notes ? <p className="mt-1.5 text-xs text-muted">{plan.notes}</p> : null}
        </>
      )}
    </div>
  );
}

// ── form ───────────────────────────────────────────────────────

interface SaveInput {
  styleId: string;
  unit: string | null;
  supervisor: string | null;
  plannedStartDate: string | null;
  targetDate: string | null;
  dailyTargetPcs: number | null;
  notes: string | null;
}

interface PlanFormProps {
  styleId: string;
  plan: {
    unit: string | null;
    supervisor: string | null;
    plannedStartDate: string | null;
    targetDate: string | null;
    dailyTargetPcs: number | null;
    notes: string | null;
  } | null;
  pending: boolean;
  onCancel: () => void;
  onSave: (v: SaveInput) => void;
}

function PlanForm({ styleId, plan, pending, onCancel, onSave }: PlanFormProps) {
  const [unit, setUnit] = useState(plan?.unit ?? "");
  const [supervisor, setSupervisor] = useState(plan?.supervisor ?? "");
  const [plannedStartDate, setStart] = useState(plan?.plannedStartDate ?? "");
  const [targetDate, setTarget] = useState(plan?.targetDate ?? "");
  const [dailyTargetPcs, setDaily] = useState(plan?.dailyTargetPcs !== null && plan?.dailyTargetPcs !== undefined ? String(plan.dailyTargetPcs) : "");
  const [notes, setNotes] = useState(plan?.notes ?? "");

  return (
    <div className="rounded-md border border-line bg-bg p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted">
          <CalendarClock size={12} aria-hidden /> Production plan
        </div>
        <button type="button" className="tag-add" onClick={onCancel}>
          <X size={10} aria-hidden /> Cancel
        </button>
      </div>
      <div className="mt-2 flex flex-wrap items-end gap-3">
        <Field label="Unit / line">
          <Input value={unit} onChange={(e) => setUnit(e.currentTarget.value)} placeholder="Unit 2" className="h-8 w-28 text-xs" />
        </Field>
        <Field label="Supervisor">
          <Input value={supervisor} onChange={(e) => setSupervisor(e.currentTarget.value)} placeholder="R. Kumar" className="h-8 w-32 text-xs" />
        </Field>
        <Field label="Planned start">
          <input type="date" value={plannedStartDate} onChange={(e) => setStart(e.currentTarget.value)} className={dateCls} />
        </Field>
        <Field label="Target date">
          <input type="date" value={targetDate} onChange={(e) => setTarget(e.currentTarget.value)} className={dateCls} />
        </Field>
        <Field label="Daily target (pcs)">
          <Input value={dailyTargetPcs} onChange={(e) => setDaily(e.currentTarget.value)} placeholder="250" inputMode="numeric" className="h-8 w-24 text-xs" />
        </Field>
        <Field label="Notes">
          <Input value={notes} onChange={(e) => setNotes(e.currentTarget.value)} placeholder="2 lines, single shift" className="h-8 w-44 text-xs" />
        </Field>
        <Button
          size="sm"
          disabled={pending}
          onClick={() =>
            onSave({
              styleId,
              unit: unit.trim() || null,
              supervisor: supervisor.trim() || null,
              plannedStartDate: plannedStartDate || null,
              targetDate: targetDate || null,
              dailyTargetPcs: parseIntOrNull(dailyTargetPcs),
              notes: notes.trim() || null,
            })
          }
        >
          {pending ? "Saving…" : "Save plan"}
        </Button>
      </div>
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────

const dateCls = "h-8 rounded-sm border border-line bg-paper px-2 font-mono text-xs text-ink outline-none focus-visible:border-sky-600";

function PlanField({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <span className="text-xs uppercase tracking-wider text-muted">{label}</span>
      <div className="font-medium text-ink">{value ?? "—"}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wider text-muted">{label}</span>
      {children}
    </label>
  );
}

interface Pace {
  daysToTarget: number | null;
  requiredDailyRate: number | null;
  onTrack: boolean | null;
  overdue: boolean;
}

/** Required pieces/day to clear the balance by the target, vs the planned rate. */
function computePace(balanceToFinish: number, targetDate: string | null, dailyTargetPcs: number | null): Pace {
  if (!targetDate) return { daysToTarget: null, requiredDailyRate: null, onTrack: null, overdue: false };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${targetDate}T00:00:00`);
  const daysToTarget = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  if (balanceToFinish <= 0) return { daysToTarget, requiredDailyRate: 0, onTrack: true, overdue: false };
  if (daysToTarget <= 0) return { daysToTarget, requiredDailyRate: null, onTrack: false, overdue: true };
  const requiredDailyRate = Math.ceil(balanceToFinish / daysToTarget);
  const onTrack = dailyTargetPcs === null ? null : dailyTargetPcs >= requiredDailyRate;
  return { daysToTarget, requiredDailyRate, onTrack, overdue: false };
}

function parseIntOrNull(raw: string): number | null {
  const t = raw.trim();
  if (t === "") return null;
  const n = Math.round(Number(t));
  return Number.isFinite(n) ? n : null;
}

function fmtDate(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}
